import React from "react";
import { Mail, Send } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { useSEO } from "../hooks/useSEO";

const PAGES = {
  "/about/": {
    title: "About Us | Morning Sunday Teer",
    description:
      "Learn about Morning Sunday Teer — an independent informational platform providing fast, accurate result, previous result and common number references.",
    h1: "About Morning Sunday Teer",
    blocks: [
      {
        h: "Who we are",
        p: "Morning Sunday Teer is an independent informational platform. We publish the latest Morning Sunday Teer result, previous result charts and daily common number references in a clean, fast and mobile-friendly format.",
      },
      {
        h: "What we do",
        p: "Our goal is to make result information easy to access. We display the First Round (10:30 AM) and Second Round (11:30 AM) numbers, maintain a historical archive, and generate a stable daily common number for reference.",
      },
      {
        h: "What we are not",
        p: "We do not operate, organize, sell tickets for, or facilitate any game or lottery. We are not an official website of any organizing body. All content is provided for general information and entertainment reference only.",
      },
    ],
  },
  "/contact/": {
    title: "Contact Us | Morning Sunday Teer",
    description:
      "Contact Morning Sunday Teer for feedback, corrections or general queries. Reach us by email or messaging.",
    h1: "Contact Us",
    contact: true,
    blocks: [
      {
        h: "Get in touch",
        p: "Have feedback, a correction or a general query? We'd love to hear from you. Use the contact details below and we'll respond as soon as possible.",
      },
    ],
  },
  "/privacy-policy/": {
    title: "Privacy Policy | Morning Sunday Teer",
    description:
      "Read the Morning Sunday Teer privacy policy covering the limited information we collect and how it is used.",
    h1: "Privacy Policy",
    blocks: [
      {
        h: "Information we collect",
        p: "We aim to collect as little personal information as possible. We may use standard, privacy-respecting analytics to understand aggregate traffic and improve the website. We do not sell personal information.",
      },
      {
        h: "Cookies & local storage",
        p: "We use browser local storage to cache the latest result so pages load instantly on refresh. This data stays on your device and is used only to improve performance.",
      },
      {
        h: "Third-party links",
        p: "Our pages may link to external sites. We are not responsible for the privacy practices or content of those sites. Please review their policies separately.",
      },
      {
        h: "Contact",
        p: "For any privacy-related questions, contact us at shillongmorningteer4758@gmail.com.",
      },
    ],
  },
  "/terms-and-conditions/": {
    title: "Terms & Conditions | Morning Sunday Teer",
    description:
      "Review the terms and conditions for using the Morning Sunday Teer informational website.",
    h1: "Terms & Conditions",
    blocks: [
      {
        h: "Acceptance of terms",
        p: "By using this website you agree to these terms. If you do not agree, please discontinue use. We may update these terms from time to time.",
      },
      {
        h: "Informational use only",
        p: "All content — including results, previous results and common numbers — is provided for general information and entertainment reference only, without any warranty of accuracy, completeness or timeliness.",
      },
      {
        h: "No facilitation of gaming",
        p: "This website does not operate, organize, sell tickets for, or facilitate any game or lottery. We are not responsible for any decisions made based on the information shown here.",
      },
      {
        h: "Limitation of liability",
        p: "To the maximum extent permitted by law, we are not liable for any loss arising from the use of, or reliance on, information provided on this website.",
      },
    ],
  },
  "/disclaimer/": {
    title: "Disclaimer | Morning Sunday Teer",
    description:
      "Important disclaimer for Morning Sunday Teer. This is an informational platform only and does not facilitate any game.",
    h1: "Disclaimer",
    blocks: [
      {
        h: "Informational platform",
        p: "Morning Sunday Teer is an informational platform only. The numbers and content displayed are for general information and entertainment reference and do not guarantee any outcome.",
      },
      {
        h: "No official affiliation",
        p: "We do not claim to be an official website. We do not operate, organize, sell tickets for, or facilitate any game, lottery or gambling activity of any kind.",
      },
      {
        h: "Accuracy",
        p: "While we strive to display accurate and timely results from our data source, we make no warranty regarding accuracy or availability. Always verify important information from an authorized source.",
      },
      {
        h: "Responsible use",
        p: "Please use this information responsibly. If gaming is restricted in your region, do not use this information to participate in any prohibited activity.",
      },
    ],
  },
};

export default function StaticPage({ slug }) {
  const cfg = PAGES[slug];
  useSEO({ title: cfg.title, description: cfg.description, path: slug });

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumbs items={[{ label: cfg.h1 }]} />
      <h1 className="font-head font-black text-3xl sm:text-4xl text-slate-900 mb-6">{cfg.h1}</h1>

      <div className="space-y-6">
        {cfg.blocks.map((b, i) => (
          <div key={i}>
            <h2 className="font-head font-bold text-lg text-slate-900 mb-1.5">{b.h}</h2>
            <p className="text-slate-600 text-[15px] leading-relaxed">{b.p}</p>
          </div>
        ))}
      </div>

      {cfg.contact && (
        <div className="mt-8 bg-white rounded-xl border border-slate-200 card-shadow p-6 space-y-4">
          <a href="mailto:shillongmorningteer4758@gmail.com" className="flex items-center gap-3 text-slate-800 font-semibold">
            <span className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <Mail size={18} className="text-[#ff4500]" />
            </span>
            shillongmorningteer4758@gmail.com
          </a>
          <a
            href="https://t.me/MorningSundayTeer"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 text-slate-800 font-semibold hover:text-[#ff4500]"
          >
            <span className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <Send size={18} className="text-[#ff4500]" />
            </span>
            @MorningSundayTeer
          </a>
        </div>
      )}
    </div>
  );
}
